import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { BlogPost, ArticleContent } from '../../types';
import { mockPosts } from '../../data/mockData';
import RelatedPosts from '../RelatedPosts/RelatedPosts';
import './BlogDetail.css';

interface BlogDetailProps {
  posts?: BlogPost[];
}

const BlogDetail: React.FC<BlogDetailProps> = ({ posts = mockPosts }) => {
  const { slug } = useParams<{ slug: string }>();
  
  // Find the blog post by slug
  const post = posts.find(p => p.slug === slug);
  
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Get related posts (exclude current post)
  // First try to get posts from the same category
  let relatedPosts = posts
    .filter(p => p.id !== post.id && p.category.id === post.category.id)
    .slice(0, 2);
  
  // If we don't have enough posts from the same category, add posts from other categories
  if (relatedPosts.length < 2) {
    const remainingPosts = posts
      .filter(p => p.id !== post.id && p.category.id !== post.category.id)
      .slice(0, 2 - relatedPosts.length);
    relatedPosts = [...relatedPosts, ...remainingPosts];
  }

  const renderArticleSection = (section: ArticleContent['sections'][0]) => {
    switch (section.type) {
      case 'text':
        return (
          <div key={section.id} className="article-section">
            {section.title && <h2 className="article-section__title">{section.title}</h2>}
            <div className="article-section__content">
              {section.content.split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
        );
      
      case 'image':
        return (
          <figure key={section.id} className="article-figure">
            <img
              src={section.image?.url}
              alt={section.image?.alt}
              className="article-figure__image"
              loading="lazy"
            />
            {section.image?.caption && (
              <figcaption className="article-figure__caption">
                {section.image.caption}
              </figcaption>
            )}
          </figure>
        );
      
      case 'text-with-image':
        return (
          <div key={section.id} className="article-section article-section--with-image">
            {section.title && <h2 className="article-section__title">{section.title}</h2>}
            <div className="article-section__content">
              {section.content.split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
            {section.image && (
              <figure className="article-figure">
                <img
                  src={section.image.url}
                  alt={section.image.alt}
                  className="article-figure__image"
                  loading="lazy"
                />
                {section.image.caption && (
                  <figcaption className="article-figure__caption">
                    {section.image.caption}
                  </figcaption>
                )}
              </figure>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="blog-detail">
      {/* Article Header - Now at the top */}
      <div className="blog-detail__header">
        <div className="blog-detail__container">
          <div className="blog-detail__meta">
            <span className="category-text">{post.category.name}</span>
          </div>
          <h1 className="blog-detail__title">{post.title}</h1>
        </div>
      </div>

      {/* Hero Section - Now below title */}
      <div className="blog-detail__hero">
        <img
          src={post.featuredImage.url}
          alt={post.featuredImage.alt}
          className="blog-detail__hero-image"
        />
      </div>

      {/* Article Content */}
      <div className="blog-detail__content">
        <div className="blog-detail__container">
          <div className="article-content">
            
            {/* Introduction */}
            {post.articleContent?.introduction && (
              <div className="article-introduction">
                {post.articleContent.introduction.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            )}

            {/* Article Sections */}
            {post.articleContent?.sections.map(renderArticleSection)}

            {/* Conclusion */}
            {post.articleContent?.conclusion && (
              <div className="article-conclusion">
                {post.articleContent.conclusion.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Related Posts Section */}
      {relatedPosts.length > 0 && (
        <div className="blog-detail__related">
          <div className="blog-detail__container">
            <RelatedPosts posts={relatedPosts} />
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogDetail;